import type { MarketStatus } from "@/features/betslip/types";
import type { QuaddieMarket } from "@/hooks/firestore/betting/useQuaddieMarkets";
import { isMarketTrading } from "utilities/sharedBettingUtilities";

// The market itself isn't taking bets - `isMarketTrading` owns that list, and
// an unknown status reads as active. Whether THIS user may bet a live one is a
// different question, answered by `bettable` in the store.
export const isMarketInactive = (status: MarketStatus | undefined): boolean =>
  status !== undefined && !isMarketTrading(status);

// DEACTIVATED is the quaddie feed saying the race jumped. Not LIVE - that's
// in-play betting, and it would blame the user's permission for the block.
export const mapQuaddieMarketStatus = (
  status: QuaddieMarket["status"] | undefined,
): MarketStatus => {
  if (status === undefined) return "ACTIVE";
  if (status === "DEACTIVATED") return "UNAVAILABLE";
  return status;
};

// A suspend is a temporary hold, unlike the other inactive statuses - the
// market comes back. Display only: it's still inactive everywhere else.
export const isMarketSuspended = (status: MarketStatus | undefined) =>
  status === "SUSPENDED";

type OutcomeStatusInput = {
  result?: string | null;
  active?: boolean | null;
  odds?: number | null;
  marketStatus?: MarketStatus;
};

// The effective status of a selection's outcome, not just its market's status.
// An outcome reads as inactive when it has a decided result (settled or
// scratched), when it's been deactivated, or - for non-racing markets - when
// it's priced at evens (the feed's "no market" signal). This is how a
// scratched runner becomes inactive: scratching flips the outcome's own
// active/result flags while the parent market can still say ACTIVE.
export const deriveOutcomeStatus = (
  outcome: OutcomeStatusInput | undefined,
  sourceType: string,
): MarketStatus => {
  if (outcome === undefined) return "ACTIVE";
  if (outcome.result !== "UNDECIDED") return "UNAVAILABLE";
  if (!outcome.active) return "UNAVAILABLE";
  if (!["race", "exotic"].includes(sourceType) && outcome.odds === 1)
    return "UNAVAILABLE";
  // Live isn't folded in here - LIVE and ACTIVE both trade, and whether this
  // user may bet a live market is worked out in `liveViewOnly`.
  return outcome.marketStatus ?? "UNAVAILABLE";
};
